import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconPlayerPauseFilled, IconPlayerPlayFilled, IconVolume, IconVolumeOff } from '@tabler/icons-react';
import { paintFilmstrip } from '../../../render/video';
import { VIDEO_LIMITS } from '../../../model/schema';
import { clipEnd, clipLanes, clipLength, moveClip, snapTime, snapTimes, trimEdge } from '../../../model/timeline';
import { layerName } from '../ToolPanel/panels/LayersPanel';
import './styles.scss';

const idle = { time: 0, playing: false, duration: 0, muted: false };
const noopSubscribe = () => () => {};
// The page's playback state, re-read on every tick.
export function usePlaybackState(playback) {
  return useSyncExternalStore(playback ? playback.subscribe : noopSubscribe, playback ? playback.getState : () => idle);
}

// "0:04,2": minutes, seconds and tenths, in the reader's decimal mark.
export function clockLabel(seconds, language) {
  const safe = Math.max(0, seconds || 0);
  const minutes = Math.floor(safe / 60);
  const rest = new Intl.NumberFormat(language, { minimumFractionDigits: 1, maximumFractionDigits: 1, minimumIntegerDigits: 2 }).format(Math.floor((safe - minutes * 60) * 10) / 10);
  return `${minutes}:${rest}`;
}
// "4,2 s".
export const secondsLabel = (seconds, language) => `${new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(Math.round(seconds * 10) / 10)} s`;

// A clip's frames across its bar, from the part of the file it plays (a drag settles first).
function ClipStrip({ layer, width }) {
  const canvas = useRef(null);
  const { url, width: fileWidth, height: fileHeight } = layer.source;
  const { start, end } = layer.trim;
  useEffect(() => {
    const element = canvas.current;
    if (!element || !url || width < 8) return undefined;
    let stop = null;
    const timer = setTimeout(() => {
      const tile = Math.max(24, Math.round((element.clientHeight || 36) * (fileWidth / fileHeight)));
      stop = paintFilmstrip(element, url, { from: start, to: end, count: Math.max(1, Math.min(14, Math.ceil(width / tile))), maxSide: 160 });
    }, 160);
    return () => { clearTimeout(timer); stop?.(); };
  }, [url, start, end, width, fileWidth, fileHeight]);
  return <canvas ref={canvas} className="art-timeline__strip" aria-hidden="true" />;
}

// The page's timeline (2.13.0), under the stage while the page has videos: play and pause,
// the time, the preview's sound and, across the page's seconds, its clips. Clips that never
// overlap share a row (a background's joined clips), the rest take one each. A clip is
// selected by a press, moved by a drag, cut by dragging an edge (both snap to the page's
// start, the playhead and the other clips' edges) and opens its trim dialog with a double
// click or Enter. Pressing the ruler moves the playhead.
export default function Timeline({ page, playback, selectedId = null, readOnly = false, onSelect, onMove, onRetrim, onTrim }) {
  const { t, i18n } = useTranslation('artEditor');
  const state = usePlaybackState(playback);
  const track = useRef(null);
  const [width, setWidth] = useState(0);
  const [draft, setDraft] = useState(null);
  const drag = useRef(null);
  useLayoutEffect(() => {
    const element = track.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const lanes = clipLanes(page);
  const shown = layer => (draft?.id === layer.id ? { ...layer, ...draft.patch } : layer);
  const reach = Math.max(state.duration, ...lanes.flat().map(layer => clipEnd(shown(layer))));
  // The seconds the track spans: the page's clips with room after them, 10 to 60.
  const span = Math.min(VIDEO_LIMITS.pageSeconds, Math.max(10, Math.ceil(reach * 1.15 + 1)));
  const perSecond = width ? width / span : 0;
  const label = 1 * perSecond >= 44 ? 1 : 5 * perSecond >= 44 ? 5 : 10;
  const ticks = perSecond ? Array.from({ length: Math.floor(span / label) + 1 }, (_, index) => index * label) : [];

  const seekAt = clientX => {
    const rect = track.current.getBoundingClientRect();
    playback?.seek(((clientX - rect.left) / rect.width) * span);
  };
  const onRulerDown = event => {
    if (event.button !== 0 || !perSecond) return;
    event.preventDefault();
    playback?.pause();
    seekAt(event.clientX);
    drag.current = { mode: 'scrub' };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onClipDown = (event, layer, mode) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    onSelect?.(layer.id);
    if (readOnly || layer.locked || !perSecond) return;
    drag.current = { mode, layer, x: event.clientX, moved: false, times: snapTimes(page, layer.id, playback ? state.time : null), patch: null };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onMoveDrag = event => {
    const current = drag.current;
    if (!current) return;
    if (current.mode === 'scrub') { seekAt(event.clientX); return; }
    const dx = event.clientX - current.x;
    if (!current.moved && Math.abs(dx) < 3) return;
    current.moved = true;
    const { layer } = current;
    const delta = dx / perSecond;
    const tolerance = 6 / perSecond;
    let patch;
    if (current.mode === 'move') {
      let at = layer.at + delta;
      const start = snapTime(at, current.times, tolerance);
      const end = snapTime(at + clipLength(layer), current.times, tolerance);
      if (start !== null) at = start; else if (end !== null) at = end - clipLength(layer);
      patch = moveClip(layer, at);
    } else {
      const edge = current.mode === 'start' ? layer.at : clipEnd(layer);
      const snapped = snapTime(edge + delta, current.times, tolerance);
      patch = trimEdge(layer, current.mode, (snapped === null ? edge + delta : snapped) - edge);
    }
    current.patch = patch;
    setDraft({ id: layer.id, patch });
  };
  const onUp = event => {
    const current = drag.current;
    drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDraft(null);
    if (!current?.moved || !current.patch) return;
    if (current.mode === 'move') onMove?.(current.layer.id, current.patch);
    else onRetrim?.(current.layer.id, current.patch);
  };
  const drags = { onPointerMove: onMoveDrag, onPointerUp: onUp, onPointerCancel: onUp };

  return <section className="art-timeline" aria-label={t('timeline')}>
    <div className="art-timeline__controls">
      <Button size="small" variant="rounded" color="inverse" preventLoading icon={state.playing ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
        aria-label={t(state.playing ? 'pause' : 'play')} tooltip={`${t(state.playing ? 'pause' : 'play')} · ${t('spaceKey')}`} disabled={!state.duration} onClick={() => playback?.toggle()} />
      <Text.caption contrast={8} className="art-timeline__clock" aria-live="off">{`${clockLabel(state.time, i18n.language)} / ${clockLabel(state.duration, i18n.language)}`}</Text.caption>
      <Button size="micro" variant="text" color="overlay" preventLoading icon={state.muted ? <IconVolumeOff /> : <IconVolume />}
        aria-pressed={state.muted} aria-label={t(state.muted ? 'previewSoundOn' : 'previewSoundOff')} tooltip={t(state.muted ? 'previewSoundOn' : 'previewSoundOff')} onClick={() => playback?.setMuted(!state.muted)} />
    </div>
    <div className="art-timeline__body">
      <div ref={track} className="art-timeline__track">
        <div className="art-timeline__ruler" onPointerDown={onRulerDown} {...drags} role="slider" tabIndex={-1} aria-label={t('playhead')}
          aria-valuemin={0} aria-valuemax={Math.round(state.duration * 10) / 10} aria-valuenow={Math.round(state.time * 10) / 10} aria-valuetext={clockLabel(state.time, i18n.language)}>
          {ticks.map(second => <span key={second} className="art-timeline__tick" style={{ left: second * perSecond }}>
            <Text.caption contrast={5}>{second === 0 ? '0' : `${second}s`}</Text.caption>
          </span>)}
        </div>
        <div className="art-timeline__lanes">
          {lanes.map((lane, index) => <div key={index} className="art-timeline__lane">
            {lane.map(source => {
              const layer = shown(source);
              const left = layer.at * perSecond;
              const size = Math.max(6, clipLength(layer) * perSecond);
              const selected = layer.id === selectedId;
              return <div key={layer.id} className={`art-timeline__clip${selected ? ' is-selected' : ''}${layer.visible ? '' : ' is-hidden'}${draft?.id === layer.id ? ' is-dragging' : ''}`}
                style={{ left, width: size }} role="button" tabIndex={0} aria-pressed={selected}
                aria-label={t('clipLabel', { name: layerName(layer, t), from: clockLabel(layer.at, i18n.language), length: secondsLabel(clipLength(layer), i18n.language) })}
                onPointerDown={event => onClipDown(event, source, 'move')} {...drags} onDoubleClick={() => !readOnly && onTrim?.(source)}
                onKeyDown={event => { if (event.key === 'Enter' && !readOnly) { event.preventDefault(); onTrim?.(source); } }}>
                <ClipStrip layer={layer} width={size} />
                <span className="art-timeline__clip-label"><Text.caption contrast={9}>{secondsLabel(clipLength(layer), i18n.language)}</Text.caption></span>
                {!readOnly && !layer.locked && <>
                  {/* Their moves bubble to the clip's handlers. */}
                  <span className="art-timeline__handle is-start" onPointerDown={event => onClipDown(event, source, 'start')} aria-hidden="true" />
                  <span className="art-timeline__handle is-end" onPointerDown={event => onClipDown(event, source, 'end')} aria-hidden="true" />
                </>}
              </div>;
            })}
          </div>)}
        </div>
        {perSecond > 0 && <span className="art-timeline__playhead" style={{ left: Math.min(state.time, span) * perSecond }} aria-hidden="true" />}
      </div>
      <Text.caption contrast={5} className="art-timeline__limit">{t('timelineLimit', { seconds: VIDEO_LIMITS.pageSeconds })}</Text.caption>
    </div>
  </section>;
}
